import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Flex,
  Text,
  Box,
  Badge,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

const VideoTracksModal = ({ isOpen, onClose, videoId, videoTitle }) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchVideoTracks = useCallback(async () => {
    if (!videoId) return;

    setLoading(true);
    try {
      // 1. video_to_track 테이블에서 해당 video_id의 track_id들 조회
      const { data: videoTracks, error: videoTracksError } = await supabase
        .from("video_to_track")
        .select("track_id")
        .eq("video_id", videoId);

      if (videoTracksError) {
        throw videoTracksError;
      }

      if (!videoTracks || videoTracks.length === 0) {
        setTracks([]);
        setLoading(false);
        return;
      }

      // 2. track_id들을 추출
      const trackIds = videoTracks.map((vt) => vt.track_id);

      // 3. track 테이블에서 해당 track_id들의 데이터 조회
      const { data: trackData, error: trackError } = await supabase
        .from("track")
        .select("*")
        .in("id", trackIds)
        .order("created_date", { ascending: false });

      if (trackError) {
        throw trackError;
      }

      setTracks(trackData || []);
    } catch (error) {
      console.error("트랙 정보 조회 실패:", error);
      toast({
        title: "트랙 정보 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [videoId, toast]);

  useEffect(() => {
    if (isOpen && videoId) {
      fetchVideoTracks();
    }
  }, [isOpen, videoId, fetchVideoTracks]);

  const handleDeleteTrack = async (trackId) => {
    try {
      const { error } = await supabase
        .from("video_to_track")
        .delete()
        .eq("video_id", videoId)
        .eq("track_id", trackId);
      if (error) throw error;
      toast({
        title: "트랙 연결 삭제 완료",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
      fetchVideoTracks();
    } catch (error) {
      toast({
        title: "트랙 연결 삭제 실패",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const displayColumns = [
    { key: "track_name_kr", label: "트랙명" },
    { key: "isrc", label: "ISRC" },
    { key: "track_code", label: "트랙코드" },
    { key: "album", label: "앨범명" },
    { key: "musician", label: "뮤지션" },
    { key: "genre_depth4", label: "장르" },
    { key: "commission_rate", label: "수익률" },
    { key: "actions", label: "" },
  ];

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return "-";
    if (key === "commission_rate") {
      return value ? `${value}%` : "-";
    }
    return String(value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>트랙 정보 - {videoTitle || "영상"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner />
            </Flex>
          ) : tracks.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500" fontSize="lg">
                연결된 트랙이 없습니다.
              </Text>
              <Text color="gray.400" fontSize="sm" mt={2}>
                트랙 추가 버튼을 통해 트랙을 연결할 수 있습니다.
              </Text>
            </Box>
          ) : (
            <Box>
              <Text fontWeight="bold" mb={4}>
                연결된 트랙 ({tracks.length}개)
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    {displayColumns.map((col) => (
                      <Th key={col.key}>{col.label}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {tracks.map((track) => (
                    <Tr key={track.id}>
                      {displayColumns.map((col) => (
                        <Td key={col.key}>
                          {(() => {
                            if (col.key === "actions") {
                              return (
                                <Button
                                  size="xs"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => handleDeleteTrack(track.id)}
                                >
                                  삭제
                                </Button>
                              );
                            }
                            const value = track[col.key];
                            if (col.key === "genre_depth4") {
                              return value ? (
                                <Badge colorScheme="cyan">{value}</Badge>
                              ) : (
                                "-"
                              );
                            }
                            return (
                              <Text noOfLines={1}>
                                {formatValue(value, col.key)}
                              </Text>
                            );
                          })()}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VideoTracksModal;
